import { useEffect, useRef, useState, type RefObject } from 'react'
import { fetchUnitDetail } from '@/api/fetchCollection'
import type { ApiUnit } from '@/types/api'

const SIGHTMAP_API_SRC = 'https://sightmap.com/embed/api.js'

/** Track script loading state at module level so multiple hook instances share one load. */
let scriptLoadPromise: Promise<void> | null = null

function loadSightMapScript (): Promise<void> {
	if (scriptLoadPromise) return scriptLoadPromise

	// Script may already exist in the DOM (e.g. injected by WP template).
	if (typeof SightMap !== 'undefined') {
		scriptLoadPromise = Promise.resolve()
		return scriptLoadPromise
	}

	scriptLoadPromise = new Promise((resolve, reject) => {
		const existing = document.querySelector(`script[src="${SIGHTMAP_API_SRC}"]`)
		if (existing) {
			existing.addEventListener('load', () => resolve())
			// If the script already loaded before we attached the listener, check global.
			if (typeof SightMap !== 'undefined') resolve()
			return
		}

		const script = document.createElement('script')
		script.src = SIGHTMAP_API_SRC
		script.async = true
		script.onload = () => resolve()
		script.onerror = () => reject(new Error('Failed to load SightMap embed API'))
		document.head.appendChild(script)
	})

	return scriptLoadPromise
}

/** How long after an embed command a focus steal is still attributed to it. */
const FOCUS_STEAL_WINDOW_MS = 1000
/** The embed can re-apply its scroll a few ms after the first one. */
const FOCUS_STEAL_TAIL_MS = 250

/**
 * Run an embed command, undoing the focus move and host-page scroll when the embed
 * focuses its own content in response (setUnitIdMatches does in current Chrome).
 */
function preventEmbedFocusSteal (iframe: HTMLIFrameElement, command: () => void): void {
	if (document.activeElement === iframe) {
		command()
		return
	}
	const previous = document.activeElement
	const savedX = window.scrollX
	const savedY = window.scrollY
	let stolen = false
	let timer = 0

	function stop () {
		window.removeEventListener('scroll', onScroll)
	}

	function onScroll () {
		if (!stolen) {
			if (document.activeElement !== iframe) return
			stolen = true
			if (previous instanceof HTMLElement && previous !== document.body && previous.isConnected) {
				previous.focus({ preventScroll: true })
			} else {
				iframe.blur()
			}
			clearTimeout(timer)
			timer = window.setTimeout(stop, FOCUS_STEAL_TAIL_MS)
		}
		if (window.scrollX !== savedX || window.scrollY !== savedY) {
			window.scrollTo({ left: savedX, top: savedY, behavior: 'instant' })
		}
	}

	timer = window.setTimeout(stop, FOCUS_STEAL_WINDOW_MS)
	window.addEventListener('scroll', onScroll, { passive: true })
	command()
}

interface UseSightMapResult {
	embed: SightMap.Embed | null
	isReady: boolean
	tooltipUnit: ApiUnit | null
	tooltipPos: { x: number; y: number } | null
}

/**
 * Hook that dynamically loads the SightMap embed API script, initialises a
 * `SightMap.Embed` instance targeting the given iframe ref, and disables the
 * built-in filters UI once the embed is ready.
 *
 * Returns `{ embed, isReady, tooltipUnit, tooltipPos }` — `embed` is `null`
 * until both the script and the iframe are available; `isReady` flips to
 * `true` after the `'ready'` event fires. `tooltipUnit` and `tooltipPos`
 * provide the data and cursor position for the map hover tooltip.
 *
 * The initialisation effect depends only on `sightmapEmbedUrl` — it does NOT
 * re-run when `unitIds` changes. A separate effect pushes updated unit IDs
 * to the live embed instance via `setUnitIdMatches()`, avoiding expensive
 * iframe teardown/rebuild on every filter change.
 *
 * No-ops when `sightmapEmbedUrl` is falsy (dev mode / unconfigured assets).
 */
export function useSightMap (
	iframeRef: RefObject<HTMLIFrameElement | null>,
	sightmapEmbedUrl: string,
	unitIds: (string | number)[] = [],
	locateUnitId?: string | number,
	onUnitClick?: (unitId: string) => void,
	mockUnits?: ApiUnit[],
	selectedFloorId?: string,
	onFloorChange?: (floorId: string) => void,
	showFloorPicker = false
): UseSightMapResult {
	const [isReady, setIsReady] = useState(false)
	const embedRef = useRef<SightMap.Embed | null>(null)

	// Tooltip state
	const [tooltipUnit, setTooltipUnit] = useState<ApiUnit | null>(null)
	const [tooltipPos, setTooltipPos] = useState<{ x: number; y: number } | null>(null)
	const hoveredUnitIdRef = useRef<string | null>(null)
	const unitCacheRef = useRef(new Map<string, ApiUnit>())

	// Capture unitIds/locateUnitId in refs so the init effect can read the
	// current values at ready-time without depending on them.
	const unitIdsRef = useRef(unitIds)
	unitIdsRef.current = unitIds

	const locateRef = useRef(locateUnitId)
	locateRef.current = locateUnitId

	const onUnitClickRef = useRef(onUnitClick)
	onUnitClickRef.current = onUnitClick

	const mockUnitsRef = useRef(mockUnits)
	mockUnitsRef.current = mockUnits

	const onFloorChangeRef = useRef(onFloorChange)
	onFloorChangeRef.current = onFloorChange

	// Floor the embed last reported, so picker state synced from it isn't echoed back via setFloor.
	const embedFloorRef = useRef<string | null>(null)

	// ── Initialisation: load script, create embed, set initial highlights ──
	useEffect(() => {
		if (!sightmapEmbedUrl) return

		let cancelled = false

		loadSightMapScript()
			.then(() => {
				if (cancelled) return
				const iframe = iframeRef.current
				if (!iframe) return

				const embed = new SightMap.Embed(iframe)
				embedRef.current = embed

				embed.on('ready', () => {
					if (cancelled) return
					// The native floor buttons are always replaced by the Spaces floor dropdown.
					// The building-image floor picker is only kept where the caller opts in.
					const hiddenUI: SightMap.UIElement[] = ['filters', 'unitList', 'floorSelection', 'unitToolTip', 'unitDetails']
					if (!showFloorPicker) hiddenUI.push('floorPicker')
					embed.disableUI(hiddenUI)
					embed.on('unitDetails.show', (event) => {
						onUnitClickRef.current?.(String(event.data.unit.id))
					})

					// locateUnitById switches floors natively; this keeps the custom picker in step.
					embed.on('floor.change', (event) => {
						const floorId = String(event.id)
						if (floorId === '0') return
						embedFloorRef.current = floorId
						onFloorChangeRef.current?.(floorId)
					})

					// ── Tooltip: cursor-following unit popup ──
					embed.on('mapUnit.mouseEnter', (event) => {
						const unitId = String(event.data.unit.id)
						hoveredUnitIdRef.current = unitId
						setTooltipPos({ x: event.data.mouseX, y: event.data.mouseY })

						// Check cache first
						const cached = unitCacheRef.current.get(unitId)
						if (cached) {
							setTooltipUnit(cached)
							return
						}

						// Mock mode: look up from local array
						const mocks = mockUnitsRef.current
						if (mocks) {
							const found = mocks.find(u => String(u.id) === unitId)
							if (found) {
								unitCacheRef.current.set(unitId, found)
								setTooltipUnit(found)
							}
							return
						}

						// Live mode: fetch from REST endpoint
						fetchUnitDetail(unitId)
							.then(res => {
								unitCacheRef.current.set(unitId, res.data)
								// Only set if this unit is still being hovered
								if (hoveredUnitIdRef.current === unitId) {
									setTooltipUnit(res.data)
								}
							})
							.catch(() => {
								// Silently ignore — tooltip just won't show
							})
					})

					embed.on('mapUnit.mouseMove', (event) => {
     	requestAnimationFrame(() => {
							setTooltipPos({ x: event.data.mouseX, y: event.data.mouseY })
       })
   		})

					embed.on('mapUnit.mouseLeave', () => {
						hoveredUnitIdRef.current = null
						setTooltipUnit(null)
						setTooltipPos(null)
					})

					if (locateRef.current !== undefined) {
						embed.locateUnitById(locateRef.current, { filterEditing: false })
					} else if (unitIdsRef.current.length > 0) {
						preventEmbedFocusSteal(iframe, () => embed.setUnitIdMatches(unitIdsRef.current, { filterEditing: false }))
					}
					setIsReady(true)
				})
			})
			.catch(err => {
				if (!cancelled) console.error('[SPACES]', err)
			})

		return () => {
			cancelled = true
			embedRef.current = null
			embedFloorRef.current = null
			hoveredUnitIdRef.current = null
			setIsReady(false)
			setTooltipUnit(null)
			setTooltipPos(null)
		}
	// disableUI can't be undone, so a floor picker change re-inits against the
	// fresh iframe SightMapEmbed mounts for it.
	}, [sightmapEmbedUrl, iframeRef, showFloorPicker])

	// ── Dynamic updates: push new unit IDs when filters change ──
	useEffect(() => {
		const embed = embedRef.current
		const iframe = iframeRef.current
		if (!isReady || !embed || !iframe) return
		// When a locateUnitId is set we skip bulk highlights.
		if (locateUnitId !== undefined) return

		preventEmbedFocusSteal(iframe, () => embed.setUnitIdMatches(unitIds, { filterEditing: false }))
	}, [unitIds, isReady, locateUnitId, iframeRef])

	// ── Dynamic updates: switch floor when the custom floor picker changes ──
	useEffect(() => {
		if (!isReady || !embedRef.current) return
		if (selectedFloorId === undefined || selectedFloorId === '') return
		if (selectedFloorId === embedFloorRef.current) return

		embedRef.current.setFloor(selectedFloorId)
	}, [selectedFloorId, isReady])

	return { embed: embedRef.current, isReady, tooltipUnit, tooltipPos }
}
